import type { ExperienceScenario } from "@src/packages/experience";
import type { PlanAction } from "../plan.types";

export type CarrierJourneyScenario = Extract<
  ExperienceScenario,
  "carrier_application" | "carrier_screening" | "carrier_matched_legal"
>;

export type CarrierJourneySection = {
  id: string;
  stepLabel: string;
  title: string;
  body: string;
  bullets: string[];
  owner: string;
  action?: PlanAction;
};

export type CarrierJourneyModel = {
  eyebrow: string;
  title: string;
  summary: string;
  phaseLabel: string;
  connectionLabel: string;
  sections: CarrierJourneySection[];
};

type ResolveCarrierJourneyModelArgs = {
  scenario: CarrierJourneyScenario;
  hasConnection: boolean;
  hasLegalReview: boolean;
};

const ACTIONS = {
  medicalHistory: {
    label: "Open medical history",
    screen: "MedicalHistory",
  } satisfies PlanAction,
  learn: {
    label: "Learn about carrier screening",
    screen: "LearnScreen",
  } satisfies PlanAction,
  findCare: {
    label: "Find a clinic or agency",
    screen: "FindCareScreen",
  } satisfies PlanAction,
  medicalRecords: {
    label: "Open medical records",
    screen: "DocumentsScreen",
    params: { initialCategory: "medical_records" },
  } satisfies PlanAction,
  legalRecords: {
    label: "Review legal documents",
    screen: "DocumentsScreen",
    params: { initialCategory: "legal_consent" },
  } satisfies PlanAction,
  coverage: {
    label: "Review coverage questions",
    screen: "CostsOverviewScreen",
  } satisfies PlanAction,
  care: {
    label: "Open care planning",
    screen: "CareOverview",
  } satisfies PlanAction,
};

function connectionLabel({
  hasConnection,
  hasLegalReview,
}: Pick<
  ResolveCarrierJourneyModelArgs,
  "hasConnection" | "hasLegalReview"
>): string {
  if (hasLegalReview) return "Legal review";
  if (hasConnection) return "Connected";
  return "Not connected";
}

export function resolveCarrierJourneyModel({
  scenario,
  hasConnection,
  hasLegalReview,
}: ResolveCarrierJourneyModelArgs): CarrierJourneyModel {
  const sharedConnectionLabel = connectionLabel({
    hasConnection,
    hasLegalReview,
  });

  if (scenario === "carrier_screening") {
    return {
      eyebrow: "Carrier journey",
      title: "Get ready for screening",
      summary:
        "Your clinic or agency decides which checks apply. Louise keeps the questions, records, and next owner in one place.",
      phaseLabel: "Screening",
      connectionLabel: sharedConnectionLabel,
      sections: [
        {
          id: "profile",
          stepLabel: "Step 1 · Your health context",
          title: "Keep your history up to date",
          body: "Pregnancy, birth, and general medical history help your care team understand what they need to review.",
          bullets: ["Pregnancy history", "Birth history", "Medical history"],
          owner: "You",
          action: ACTIONS.medicalHistory,
        },
        {
          id: "screening-records",
          stepLabel: "Step 2 · Records",
          title: "Gather the records your program requests",
          body: "Requirements vary by clinic, agency, location, and program. Add what has been requested and keep the source visible.",
          bullets: [
            "Pregnancy and delivery records",
            "Uterine evaluation",
            "Clinic-requested labs",
          ],
          owner: "Clinic / agency",
          action: ACTIONS.medicalRecords,
        },
        {
          id: "screening-learn",
          stepLabel: "Step 3 · Understand",
          title: "Understand screening before you agree",
          body: "Learn what a test is for, who orders it, and where to take questions before making a medical decision.",
          bullets: [
            "Carrier screening",
            "Psychological evaluation",
            "Questions for your clinic",
          ],
          owner: "You + care team",
          action: ACTIONS.learn,
        },
        {
          id: "screening-care",
          stepLabel: "Step 4 · Care connection",
          title: "Keep your clinic or agency in the loop",
          body: "Louise can help you find care, but your clinic or agency owns eligibility, timing, and clearance decisions.",
          bullets: ["Clinic contact", "Agency contact", "Next review"],
          owner: "Clinic / agency",
          action: ACTIONS.findCare,
        },
      ],
    };
  }

  if (scenario === "carrier_matched_legal") {
    const hasConfirmedConnection = hasConnection || hasLegalReview;
    return {
      eyebrow: "Carrier journey",
      title: hasLegalReview
        ? "Keep your agreement and care plan moving"
        : hasConfirmedConnection
          ? "Prepare for the next handoff"
          : "Confirm your next handoff",
      summary:
        "A match is only one part of the journey. Keep consent, legal review, coverage, and clinic-owned transfer preparation visible.",
      phaseLabel: hasLegalReview
        ? "Matched · legal review"
        : hasConfirmedConnection
          ? "Matched"
          : "Connection pending",
      connectionLabel: sharedConnectionLabel,
      sections: [
        {
          id: "connection",
          stepLabel: "Step 1 · Connection",
          title: hasConnection
            ? "Your connection is visible"
            : "Confirm who is coordinating your care",
          body: hasConnection
            ? "Only a connected and permissioned relationship should appear here. Ask the agency or clinic when something looks wrong."
            : "No clinic, agency, or intended-parent connection is available yet. Start with a trusted care handoff.",
          bullets: [
            "Counterparty permission",
            "Next action owner",
            "Contact path",
          ],
          owner: hasConnection ? "Connection" : "Louise handoff",
          action: hasConnection ? ACTIONS.legalRecords : ACTIONS.findCare,
        },
        {
          id: "legal",
          stepLabel: "Step 2 · Consent and legal",
          title: "Review what needs a legal answer",
          body: "Louise can organize requests and documents. Your attorney, clinic, and agency own legal advice, parentage, and agreement approval.",
          bullets: [
            "Agreement request",
            "Consent and sharing",
            "Attorney questions",
          ],
          owner: "Legal / agency",
          action: ACTIONS.legalRecords,
        },
        {
          id: "coverage",
          stepLabel: "Step 3 · Coverage and practical planning",
          title: "Make the practical pieces explicit",
          body: "Insurance, escrow, travel, reimbursement, and support arrangements vary. Track questions without presenting an estimate as a promise.",
          bullets: [
            "Insurance review",
            "Escrow questions",
            "Travel and support",
          ],
          owner: "Agency / clinic",
          action: ACTIONS.coverage,
        },
        {
          id: "transfer-prep",
          stepLabel: "Step 4 · Transfer preparation",
          title: "Wait for a clinic-owned transfer plan",
          body: "The transfer may be frozen or fresh. Once the clinic supplies the protocol, Care can track preparation, monitoring, medication, transfer day, and the beta wait.",
          bullets: [
            "FET or fresh transfer",
            "Natural or medicated preparation",
            "Clinic dates and instructions",
          ],
          owner: "Clinic",
          action: ACTIONS.care,
        },
      ],
    };
  }

  return {
    eyebrow: "Carrier journey",
    title: "Explore the carrier path at your pace",
    summary:
      "Start with your history, learn what the process involves, and choose a clinic or agency when you are ready.",
    phaseLabel: "Exploring",
    connectionLabel: sharedConnectionLabel,
    sections: [
      {
        id: "profile",
        stepLabel: "Step 1 · Start here",
        title: "Share your health context",
        body: "Your medical history is the foundation for a thoughtful conversation with a clinic or agency. It is not a Louise approval decision.",
        bullets: ["Medical history", "Pregnancy history", "Questions for care"],
        owner: "You",
        action: ACTIONS.medicalHistory,
      },
      {
        id: "learn",
        stepLabel: "Step 2 · Understand",
        title: "Learn what becoming a carrier involves",
        body: "Take the process one step at a time: screening, matching, legal review, transfer preparation, pregnancy care, and support.",
        bullets: [
          "Roles and timelines",
          "Screening questions",
          "Support planning",
        ],
        owner: "You",
        action: ACTIONS.learn,
      },
      {
        id: "care",
        stepLabel: "Step 3 · Find care",
        title: "Choose a trusted clinic or agency path",
        body: "Louise can help you find care. Until a partner connection exists, we will not invent matches or promise compensation.",
        bullets: [
          "Clinic options",
          "Agency questions",
          "Regional availability",
        ],
        owner: "Louise handoff",
        action: ACTIONS.findCare,
      },
      {
        id: "records",
        stepLabel: "Step 4 · Prepare records",
        title: "Keep important records close",
        body: "Upload records when a clinic or agency requests them. Each document should retain its source and review status.",
        bullets: ["Medical records", "Screening results", "Consent requests"],
        owner: "You + care team",
        action: ACTIONS.medicalRecords,
      },
    ],
  };
}
